// deno-lint-ignore-file no-explicit-any no-window no-window-prefix
import { useEffect, useRef, useState } from "hono/jsx";
import { useBBConnection } from "./useBBConnection.ts";

interface BBRoomProps {
  roomId: string;
  user?: { id: string; isAnonymous?: boolean };
}

export function BBRoom({ roomId, user }: BBRoomProps) {
  const { connected, roomNotFound, setRoomNotFound, sendMessage } =
    useBBConnection({ roomId, user });

  // Debug
  // useEffect(() => {
  //     console.log("[BBRoom] Connected state changed:", connected);
  // }, [connected]);

  const [gameState, setGameState] = useState<any>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<any>(null);

  const latestStateRef = useRef<any>(null);

  useEffect(() => {
    const handleMessage = (e: CustomEvent) => {
      const msg = e.detail;

      // Capture state to replay on init if needed
      if (msg.type === "STATE") {
        latestStateRef.current = msg;
      }

      if (apiRef.current) {
        apiRef.current.handleMessage(msg);
      }
    };
    const handleStateUpdate = (e: CustomEvent) => {
      setGameState(e.detail);
    };
    window.addEventListener("bb:message", handleMessage as EventListener);
    window.addEventListener(
      "bb:state:update",
      handleStateUpdate as EventListener,
    );
    return () => {
      window.removeEventListener("bb:message", handleMessage as EventListener);
      window.removeEventListener(
        "bb:state:update",
        handleStateUpdate as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    // console.log("[BBRoom] Mounted");
    let timer: number;
    const check = () => {
      if (connected && containerRef.current) {
        // @ts-ignore: initBB is attached to window in client script
        if (window.initBB) {
          // Cleanup previous if exists (Safety)
          if (apiRef.current) {
            apiRef.current.destroy();
            // Also clear container to remove canvas
            if (containerRef.current) containerRef.current.innerHTML = "";
          }

          console.log("[BBRoom] Initializing Game...");
          // @ts-ignore: initBB is attached to window
          apiRef.current = window.initBB(containerRef.current, sendMessage);

          // Replay latest state if we missed it during load
          if (latestStateRef.current) {
            console.log("[BBRoom] Replaying missed state...");
            apiRef.current.handleMessage(latestStateRef.current);
          }
        } else {
          timer = setTimeout(check, 100);
        }
      }
    };

    if (connected) check();

    return () => {
      clearTimeout(timer);
      if (apiRef.current) {
        apiRef.current.destroy();
        apiRef.current = null;
      }
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [connected]);

  const [selectedType, setSelectedType] = useState<string | null>(null);

  const toggleBuildMode = (type: string) => {
    if (selectedType === type) {
      setSelectedType(null);
      if (apiRef.current) apiRef.current.setBuildMode(null);
    } else {
      setSelectedType(type);
      if (apiRef.current) apiRef.current.setBuildMode(type);
    }
  };

  const handleCreate = async () => {
    try {
      const res = await fetch("/api/bb/rooms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customId: roomId, isPublic: true }),
      });
      if (res.ok) {
        setRoomNotFound(false);
      }
    } catch (_err) {
      console.error(_err);
    }
  };

  if (roomNotFound) {
    const canCreate = user && !user.isAnonymous;
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-base-200 p-4 space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-4xl font-black text-primary tracking-tighter uppercase">
            Bunker Not Found
          </h2>
          <p className="opacity-60 font-bold italic">
            The bunker "{roomId}" doesn't exist yet.
          </p>
        </div>
        <div className="card w-full max-w-sm bg-base-100 shadow-2xl overflow-hidden">
          <div className="card-body items-center text-center space-y-4">
            {canCreate
              ? (
                <>
                  <div className="text-xs uppercase font-black opacity-30 tracking-widest">
                    Wanna start it?
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-lg w-full font-black uppercase text-xl h-20 shadow-xl"
                    onClick={handleCreate}
                  >
                    Establish Bunker {roomId}
                  </button>
                </>
              )
              : (
                <>
                  <div className="text-xs uppercase font-black opacity-30 tracking-widest">
                    Login Required
                  </div>
                  <div className="p-4 bg-base-200 rounded-lg w-full">
                    <p className="text-sm opacity-70">
                      You need to be logged in to establish new bunkers.
                    </p>
                  </div>
                  <a
                    href="/login"
                    className="btn btn-primary w-full font-black uppercase"
                  >
                    Login
                  </a>
                </>
              )}
            <a
              href="/bb"
              className="btn btn-ghost btn-sm opacity-50 font-black"
            >
              Back to Lobby
            </a>
          </div>
        </div>
      </div>
    );
  }

  // if (!connected) {
  //     return (
  //         <div className="flex items-center justify-center h-screen bg-base-300">
  //             <span className="loading loading-spinner loading-lg"></span>
  //         </div>
  //     );
  // }

  const roomTypes = [
    { id: "generator", label: "Generator", cost: 100, icon: "⚡" },
    { id: "kitchen", label: "Kitchen", cost: 100, icon: "🍔" },
    { id: "quarters", label: "Quarters", cost: 50, icon: "🛏️" },
    { id: "mine", label: "Mine", cost: 200, icon: "⛏️" },
    { id: "turret", label: "Turret", cost: 150, icon: "🔫" },
    { id: "lift", label: "Shaft", cost: 50, icon: "↕️" }, // Renamed from Lift
  ];

  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);

  useEffect(() => {
    const handleSelection = (e: CustomEvent) => {
      setSelectedRoomId(e.detail.roomId);
    };
    window.addEventListener("bb:ui:select", handleSelection as EventListener);
    return () =>
      window.removeEventListener(
        "bb:ui:select",
        handleSelection as EventListener,
      );
  }, []);

  const selectedRoom = gameState?.rooms?.find((r: any) =>
    r.id === selectedRoomId
  );

  const getRoomStats = (room: any) => {
    if (!room) return null;
    const vol = room.width * room.height;
    let cap = 0;
    let rate = 0;
    let resType = "";

    if (room.type === "generator") {
      cap = 100 * vol;
      rate = 10 * room.width;
      resType = "Energy";
    }
    if (room.type === "kitchen") {
      cap = 100 * vol;
      rate = 10 * room.width;
      resType = "Food";
    }
    if (room.type === "mine") {
      cap = 100 * vol;
      rate = 5 * room.width;
      resType = "Materials";
    }

    return { cap, rate, resType };
  };

  const stats = selectedRoom ? getRoomStats(selectedRoom) : null;

  return (
    <div
      ref={containerRef}
      className="w-full h-full fixed inset-0 bg-black overflow-hidden"
    >
      {/* UI Overlay */}
      <div className="absolute top-0 left-0 p-4 text-white z-10 pointer-events-none w-full flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold drop-shadow-md">
            Billionaire Bunker {roomId}
          </h1>
          {gameState && (
            <div className="mt-2 text-sm bg-black/70 p-4 rounded backdrop-blur-sm pointer-events-auto inline-block">
              <div className="grid grid-cols-2 gap-4 mb-2">
                <div>
                  <span className="text-green-400 font-bold">$</span>{" "}
                  {Math.floor(gameState.resources?.money)}{" "}
                  <span className="text-xs opacity-50">
                    / {gameState.resources?.maxMoney}
                  </span>
                </div>
                <div>
                  <span className="text-yellow-400 font-bold">⚡</span>{" "}
                  {Math.floor(gameState.resources?.energy)}{" "}
                  <span className="text-xs opacity-50">
                    / {gameState.resources?.maxEnergy}
                  </span>
                </div>
                <div>
                  <span className="text-blue-400 font-bold">📦</span>{" "}
                  {Math.floor(gameState.resources?.materials)}{" "}
                  <span className="text-xs opacity-50">
                    / {gameState.resources?.maxMaterials}
                  </span>
                </div>
                <div>
                  <span className="text-red-400 font-bold">🍔</span>{" "}
                  {Math.floor(gameState.resources?.food)}{" "}
                  <span className="text-xs opacity-50">
                    / {gameState.resources?.maxFood}
                  </span>
                </div>
              </div>
              <div className="text-xs opacity-70">
                Wave: {gameState.wave}{" "}
                ({gameState.waveActive ? "ACTIVE" : "Safe"} |{" "}
                {gameState.enemies?.length} Enemies)
              </div>
              <div className="text-xs opacity-70 mt-1">
                Workers: {gameState.workers?.length || 0}
              </div>
            </div>
          )}
        </div>

        {/* Selected Room Stats */}
        {selectedRoom && (
          <div className="bg-base-200 text-base-content p-4 rounded-xl shadow-xl pointer-events-auto backdrop-blur-md w-64 mr-4 mt-12 animate-in slide-in-from-right fade-in">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="font-black uppercase text-lg">
                  {roomTypes.find((t) => t.id === selectedRoom.type)?.label ||
                    selectedRoom.type}
                </h3>
                <div className="text-xs opacity-50 font-mono">
                  {selectedRoom.id.substring(0, 8)}...
                </div>
              </div>
              <button
                className="btn btn-ghost btn-xs circle"
                onClick={() => setSelectedRoomId(null)}
              >
                x
              </button>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="opacity-70">Level</span>
                <span className="font-bold">{selectedRoom.level}</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-70">Size</span>
                <span className="font-bold">
                  {selectedRoom.width} x {selectedRoom.height}
                </span>
              </div>
              {stats && stats.resType && (
                <>
                  <div className="divider my-1"></div>
                  <div className="flex justify-between text-success">
                    <span>+ Capacity</span>
                    <span className="font-bold">
                      +{stats.cap} {stats.resType}
                    </span>
                  </div>
                  <div className="flex justify-between text-warning">
                    <span>Production</span>
                    <span className="font-bold">+{stats.rate} / 5s</span>
                  </div>
                </>
              )}
              <div className="divider my-1"></div>
              <div className="w-full bg-base-300 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-green-500 h-full"
                  style={{
                    width: `${
                      (selectedRoom.health / selectedRoom.maxHealth) * 100
                    }%`,
                  }}
                >
                </div>
              </div>
              <div className="text-[10px] text-center opacity-50">
                Health: {selectedRoom.health}/{selectedRoom.maxHealth}
              </div>

              {selectedRoom.type === "entrance" && (
                <>
                  <div className="divider my-1"></div>
                  <button
                    className="btn btn-xs btn-outline w-full"
                    onClick={() => {
                      // Hacky local state tracking since we don't have it in server yet.
                      // We attach a map to the apiRef to track per-room state.
                      if (!apiRef.current._hatchStates) {
                        apiRef.current._hatchStates = {};
                      }
                      const currentState = !!apiRef.current
                        ._hatchStates[selectedRoom.id];
                      const newState = !currentState;

                      apiRef.current._hatchStates[selectedRoom.id] = newState;
                      apiRef.current.toggleHatch(selectedRoom.id, newState);
                    }}
                  >
                    Toggle Hatch
                  </button>
                </>
              )}

              {selectedRoom.type === "lift" &&
                (selectedRoom.mechanism !== "elevator") && (
                <>
                  <div className="divider my-1"></div>
                  <button
                    className="btn btn-xs btn-outline btn-success w-full"
                    onClick={() => {
                      sendMessage({
                        type: "UPGRADE_ROOM",
                        roomId: selectedRoom.id,
                      });
                    }}
                  >
                    Upgrade to Lift (${50 * selectedRoom.height})
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-4 left-0 w-full pointer-events-none flex justify-center">
        <div className="bg-base-200/90 p-2 rounded-xl shadow-xl pointer-events-auto flex gap-2 backdrop-blur-md overflow-x-auto max-w-[90vw]">
          {roomTypes.map((rt) => (
            <button
              key={rt.id}
              type="button"
              className={`btn btn-sm ${
                selectedType === rt.id ? "btn-primary" : "btn-ghost"
              } flex-col h-auto py-2 gap-1`}
              onClick={() => toggleBuildMode(rt.id)}
            >
              <span className="text-xl">{rt.icon}</span>
              <span className="text-xs">{rt.label}</span>
              <span className="text-[10px] opacity-70">${rt.cost}</span>
            </button>
          ))}
        </div>
      </div>

      {!connected && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-50">
          <span className="loading loading-spinner loading-lg text-primary">
          </span>
        </div>
      )}
    </div>
  );
}
